// src/DataTable.jsx
import React, { useMemo, useState } from "react";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import Papa from "papaparse";
import { flexRender } from '@tanstack/react-table';

// put at the top of DataTable component


/*
 Props:
  - data: [{ colA: "...", colB: 123, ... }, ...]
  - columnsDef (optional): array of column definitions (if omitted we infer)
  - pageSize (optional)
*/
export default function DataTable({ data = [], columnsDef = null, pageSize = 50 }) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [metadata, setMetadata] = useState(null);


  // infer columns from first row if needed
  const columns = useMemo(() => {
  const columnHelper = createColumnHelper();
 

  // determine incoming keys/defs
  const defs = columnsDef
    ? JSON.parse(JSON.stringify(columnsDef))   // deep-clone incoming defs (guard against external mutation)
    : (data[0] ? Object.keys(data[0]) : []);

  const isObjectDefs = Array.isArray(defs) && defs.length > 0 && typeof defs[0] === "object";

  // helper: parse numbers from messy strings (commas, percent, currency, NBSP)
  const parseNumber = (v) => {
    if (v === null || v === undefined) return NaN;
    const s = String(v).replace(/\u00A0/g, " ").replace(/,/g, "").replace(/[%$€£]/g, "").trim();
    // handle parentheses for negative currencies "(1,234)" => -1234
    const paren = /^\((.*)\)$/.exec(s);
    const cleaned = paren ? `-${paren[1]}` : s;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  };

  const inferTypeFromSample = (key) => {
    const sample = data && data.length ? data[0][key] : null;
    if (sample == null) return "string";
    const s = String(sample).trim();
    if (/^\(?-?[\d,]+(\.\d+)?\)?$/.test(s)) return s.indexOf(".") >= 0 ? "float" : "integer";
    if (/[%]$/.test(s)) return "percent";
    if (/^[\$\€\£]/.test(s)) return "currency";
    // naive ISO date check
    if (!Number.isNaN(Date.parse(s))) return "date";
    return "string";
  };


  return (defs || []).map((d, i) => {
	if (isObjectDefs) {
		const id = d.id || d.accessorKey || d.key || `col_${i}`;
		const title = d.header || d.title || d.Header || id;
		const accessor = d.accessorKey || d.accessor || id;
		const type = (d.type || inferTypeFromSample(accessor)).toLowerCase();



      // sorting function signature: (rowA, rowB, columnId) => number
      const sortingFn = (rowA, rowB, columnId) => {
        const aRaw = rowA.getValue(columnId);
        const bRaw = rowB.getValue(columnId);

        if (type === "integer" || type === "float" || type === "number" || type === "percent" || type === "currency") {
          const na = parseNumber(aRaw);
          const nb = parseNumber(bRaw);
          if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
          if (Number.isNaN(na)) return 1;
          if (Number.isNaN(nb)) return -1;
          return na - nb;
        }

        if (type === "date") {
          const da = Date.parse(String(aRaw));
          const db = Date.parse(String(bRaw));
          if (Number.isNaN(da) && Number.isNaN(db)) return 0;
          if (Number.isNaN(da)) return 1;
          if (Number.isNaN(db)) return -1;
          return da - db;
        }

        // default: string compare (locale-aware, numeric option)
        return String(aRaw ?? "").localeCompare(String(bRaw ?? ""), undefined, { numeric: true, sensitivity: "base" });
      };

      return columnHelper.accessor(accessor, {
		id,
		header: () => (
		  <span style={{ textTransform: "start", fontWeight: 600 }}>
			{(d && d.header
			  ? String(d.header)
			  : title
			).replace(/_/g, " ")}
		  </span>
		),
		cell: info => info.getValue() ?? "",
		enableColumnFilter: d.enableColumnFilter ?? true,
	  });
	} else {
      // defs are simple string keys
      const key = d;
      const type = inferTypeFromSample(key);
      const sortingFn = (rowA, rowB, columnId) => {
        const aRaw = rowA.getValue(columnId);
        const bRaw = rowB.getValue(columnId);
        if (type === "integer" || type === "float" || type === "number" || type === "percent" || type === "currency") {
          const na = parseNumber(aRaw);
          const nb = parseNumber(bRaw);
          if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
          if (Number.isNaN(na)) return 1;
          if (Number.isNaN(nb)) return -1;
          return na - nb;
        }
        if (type === "date") {
          const da = Date.parse(String(aRaw));
          const db = Date.parse(String(bRaw));
          if (Number.isNaN(da) && Number.isNaN(db)) return 0;
          if (Number.isNaN(da)) return 1;
          if (Number.isNaN(db)) return -1;
          return da - db;
        }
        return String(aRaw ?? "").localeCompare(String(bRaw ?? ""), undefined, { numeric: true, sensitivity: "base" });
      };

      return columnHelper.accessor(key, {
        id: key,
        header: () => <span style={{ textTransform: "start", fontWeight: 600 }}>{String(key).replace(/_/g, " ")}</span>,
        cell: info => info.getValue() ?? "",
        enableColumnFilter: true,
        sortingFn,
      });
    }
  });
}, [data, columnsDef]);

	// show first few column defs (safe, non-destructive)
	console.log('--- columns (summary) ---');
	console.table((columns || []).slice(0, 10).map(c => ({
	  id: c.id,
	  // columnDef exists on the helper-created column; show whether header is a function or a literal
	  headerIsFunction: !!(c.columnDef && typeof c.columnDef.header === 'function'),
	  headerRaw: c.columnDef ? (typeof c.columnDef.header === 'function' ? '<function>' : c.columnDef.header) : null,
	  accessor: c.accessorFn ? '<accessorFn>' : (c.id || c.accessorKey || null)
	})));



  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: "includesString", // built-in simple string filter
  });

  // CSV export
/*   const exportCsv = () => {
    if (!data || data.length === 0) return;
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }; */


  return (
    <div className="ccd-table-card">
      <div className="ccd-table-controls" style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div>
          <input
            placeholder="Search..."
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="ccd-input"
            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd" }}
          />
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={() => table.setPageIndex(0)} className="ccd-btn">First</button>
          <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} className="ccd-btn">Prev</button>
          <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} className="ccd-btn">Next</button>
          <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} className="ccd-btn">Last</button>
        </div>
      </div>

      <div className="ccd-table-wrap" style={{ overflowX: "auto", borderRadius: 8 }}>
        <table className="ccd-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    style={{
                      textAlign: "left",
                      padding: "8px 10px",
                      borderBottom: "1px solid #eee",
                      cursor: h.column.getCanSort() ? "pointer" : "default",
                      userSelect: "none",
                      background: "#fff",
                    }}
                    onClick={h.column.getToggleSortingHandler()}
                  >
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {h.isPlaceholder ? null : h.renderHeader ? h.renderHeader() : (h.Header ?? h.id)}
                      {{
                        asc: " 🔼",
                        desc: " 🔽",
                      }[h.column.getIsSorted() ?? ""] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} style={{ borderBottom: "1px solid #fafafa" }}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} style={{ padding: "8px 10px" }}>
                    { typeof cell.renderCell === 'function'
						? cell.renderCell()
						: flexRender(cell.column.columnDef.cell, cell.getContext())
					}

                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ccd-table-footer" style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: "#666" }}>
          Showing {table.getRowModel().rows.length} rows — page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            className="ccd-input"
            style={{ padding: 6 }}
          >
            {[10, 20, 50, 100].map((s) => <option key={s} value={s}>{s} / page</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}
